import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchSnowData } from './api';
import ResortEvidence from './ResortEvidence';
import './RekkieSnowPage.css';

export default function RekkieSnowPage() {
  const [params, setParams] = useSearchParams();
  const [resorts, setResorts] = useState([]);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState('');
  const [auditAttempt, setAuditAttempt] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    fetchSnowData('resorts', controller.signal).then(cohort => {
      if (controller.signal.aborted) return;
      setResorts(cohort.resorts);
    }).catch(failure => {
      if (!controller.signal.aborted) setError(failure.message);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [attempt]);

  useEffect(() => {
    const controller = new AbortController();
    setAuditLoading(true);
    setAuditError('');
    fetchSnowData('audit', controller.signal).then(report => {
      if (!controller.signal.aborted) setAudit(report.audit);
    }).catch(failure => {
      if (!controller.signal.aborted) setAuditError(failure.message);
    }).finally(() => {
      if (!controller.signal.aborted) setAuditLoading(false);
    });
    return () => controller.abort();
  }, [auditAttempt]);

  const selected = resorts.find(resort => resort.id === params.get('resort')) || resorts[0];

  return (
    <section className="rekkie-snow-page" aria-labelledby="rekkie-snow-title">
      <header className="rekkie-snow-header">
        <div>
          <h1 id="rekkie-snow-title">REKKIE Snow</h1>
          <p>2026–27 pilot · Forecasts and data readiness</p>
        </div>
        <span className="rekkie-snow-status">Pilot · Unverified</span>
      </header>
      {loading && <p role="status">Loading pilot resorts…</p>}
      {!loading && error && <div className="rekkie-snow-empty" role="alert">
        <p>{error}</p>
        <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
      </div>}
      {!loading && !error && selected && <>
        <div className="rekkie-snow-layout">
          <nav className="rekkie-snow-resorts" aria-label="Pilot resorts">
            <h2>Pilot resorts <span>{resorts.length}</span></h2>
            {resorts.map(resort => <button key={resort.id} type="button"
              aria-pressed={selected.id === resort.id}
              onClick={() => setParams(previous => {
                const next = new URLSearchParams(previous);
                next.set('resort', resort.id);
                next.delete('point');
                return next;
              })}>
              <span>{resort.name}</span><small>{resort.region}</small>
            </button>)}
          </nav>
          <section className="rekkie-snow-detail" aria-labelledby="snow-resort-name" aria-live="polite">
            <div className="rekkie-snow-detail-heading">
              <div><h2 id="snow-resort-name">{selected.name}</h2><p>{selected.region} · {selected.timezone}</p></div>
              <span className="rekkie-snow-status">Pilot resort</span>
            </div>
            <ResortEvidence key={selected.id} resort={selected} />
          </section>
        </div>
        <section className="rekkie-snow-audit" aria-labelledby="snow-audit-title">
          <div className="rekkie-snow-detail-heading">
            <h2 id="snow-audit-title">Source archive checks</h2>
            {audit && <span className="rekkie-snow-status">CONUS · sample check</span>}
          </div>
          {auditLoading ? <p role="status">Loading source checks…</p> : auditError ? <div role="alert">
            <p>{auditError}</p><button type="button" onClick={() => setAuditAttempt(value => value + 1)}>Retry source checks</button>
          </div> : !audit ? <p>No saved source checks.</p> : <>
            <p>Model initialization {new Date(audit.initializedAt).toLocaleString('en-US', { timeZone: 'UTC' })} UTC · +{audit.leadHours}h lead</p>
            <p>Checked {new Date(audit.checkedAt).toLocaleString('en-US', { timeZone: 'UTC' })} UTC · Historical publication times remain unverified.</p>
            <div className="rekkie-snow-source-grid">
              {audit.sources.map(source => <article key={source.model}>
                <div className="rekkie-snow-detail-heading"><h3>{source.model}</h3>
                  <span className="rekkie-snow-status">{source.status === 'captured' ? 'Raw field saved' : 'Check failed'}</span></div>
                {source.status === 'captured' ? <>
                  <p>Accumulation: {source.selectedField.interval}</p>
                  <p>{(source.payload.bytes / 1024).toFixed(0)} KB · SHA-256 recorded</p>
                  <a href={`${source.url}.idx`} target="_blank" rel="noreferrer">NOAA field inventory ↗</a>
                </> : <p>{source.error}</p>}
              </article>)}
            </div>
          </>}
        </section>
      </>}
    </section>
  );
}
